import React from 'react';
import { shape, string, number, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './DealerPricingBreakdown.module.css';

const { Money } = sdkTypes;

const DealerPricingBreakdown = props => {
  const { className, listing, currentUser, intl } = props;

  // Check if current user is a dealer/retailer
  const userPublicData = currentUser?.attributes?.publicData || {};
  const userProfilePublicData = currentUser?.attributes?.profile?.publicData || {};
  const userSellerType = userProfilePublicData.sellerType || userPublicData.sellerType;
  const userType = userProfilePublicData.userType || userPublicData.userType;

  // A user is a dealer if:
  // 1. They have userType 'provider' (they can sell)
  // 2. They are a manufacturer, inventory partner, or owner seller type
  // 3. OR they explicitly have businessType 'Retailer'
  const isDealer =
    (userType === 'provider' &&
      (userSellerType === 'manufacturer' || userSellerType === 'inventoryPartner')) ||
    userPublicData.businessType === 'Retailer' ||
    userSellerType === 'owner';

  // Get listing pricing data
  const listingPublicData = listing?.attributes?.publicData || {};
  const { brandBasePrice: storedBrandBasePrice, dealerMarginPercentage } = listingPublicData;
  const listingPrice = listing?.attributes?.price;

  // Only show breakdown if:
  // 1. User is a dealer
  // 2. Listing has brand base price and dealer margin configured
  if (!isDealer || !storedBrandBasePrice || !dealerMarginPercentage || !listingPrice) {
    return null;
  }

  const currency = listingPrice.currency;
  const brandBasePriceMoney = new Money(storedBrandBasePrice.amount, storedBrandBasePrice.currency);

  // Calculate dealer margin amount
  const marginAmount = Math.round((storedBrandBasePrice.amount * dealerMarginPercentage) / 100);
  const dealerMarginMoney = new Money(marginAmount, currency);

  // Calculate final price (what customer pays)
  const finalPrice = storedBrandBasePrice.amount + marginAmount;
  const finalPriceMoney = new Money(finalPrice, currency);

  const classes = classNames(css.root, className);

  return (
    <div className={classes}>
      <h3 className={css.title}>
        <FormattedMessage id="DealerPricingBreakdown.title" />
      </h3>

      <div className={css.pricingBreakdown}>
        <div className={css.priceRow}>
          <span className={css.priceLabel}>
            <FormattedMessage id="DealerPricingBreakdown.brandBasePrice" />
          </span>
          <span className={css.priceValue}>{formatMoney(intl, brandBasePriceMoney)}</span>
        </div>

        <div className={css.priceRow}>
          <span className={css.priceLabel}>
            <FormattedMessage
              id="DealerPricingBreakdown.dealerMargin"
              values={{ percentage: dealerMarginPercentage }}
            />
          </span>
          <span className={css.priceValue}>{formatMoney(intl, dealerMarginMoney)}</span>
        </div>

        <div className={classNames(css.priceRow, css.totalRow)}>
          <span className={css.priceLabel}>
            <FormattedMessage id="DealerPricingBreakdown.finalPrice" />
          </span>
          <span className={css.priceValue}>{formatMoney(intl, finalPriceMoney)}</span>
        </div>
      </div>

      <div className={css.dealerInfo}>
        <p className={css.dealerEarnings}>
          <FormattedMessage
            id="DealerPricingBreakdown.yourEarnings"
            values={{
              amount: formatMoney(intl, dealerMarginMoney),
              percentage: dealerMarginPercentage,
            }}
          />
        </p>
        <p className={css.brandEarnings}>
          <FormattedMessage
            id="DealerPricingBreakdown.brandEarnings"
            values={{ amount: formatMoney(intl, brandBasePriceMoney) }}
          />
        </p>
      </div>
    </div>
  );
};

DealerPricingBreakdown.defaultProps = {
  className: null,
  currentUser: null,
};

DealerPricingBreakdown.propTypes = {
  className: string,
  listing: object.isRequired,
  currentUser: object,
  intl: intlShape.isRequired,
};

export default injectIntl(DealerPricingBreakdown);
